// =============================================================================
// domain/accountSecurity.ts — the checks in front of a password or email change.
// -----------------------------------------------------------------------------
// ── 🚨 THESE RULES DECIDE WHAT THE USER IS TOLD, AND THE ORDER IS OBSERVABLE ──
//
// Every branch here returns a MESSAGE, and more than one can be true at once —
// a four-character password can also be mistyped, and a blank pair is also a
// mismatch. So the order these are tested in is a real behaviour, not an
// implementation detail: it decides which of two true statements the user reads.
//
// The order is the old client's, and the messages are its wording verbatim.
// Reordering them would show a different (still true) message for the same
// input, which is the kind of difference a side-by-side would never catch and a
// confused user would.
//
// ⚠ NOTHING HERE TOUCHES A PASSWORD BEYOND ITS LENGTH AND EQUALITY. No hashing,
// no storage, no logging — the values arrive from the form, are compared, and
// are handed straight to Supabase by the caller. A helper here that returned
// anything derived from the password would put it somewhere it does not belong.
// =============================================================================

/** The old client's minimum. Supabase's own floor is 6; this is stricter. */
export const MIN_PASSWORD_LENGTH = 8;

export interface PasswordChangeInput {
  current: string;
  next: string;
  confirm: string;
}

/**
 * `null` when the change may be attempted, otherwise the message to show.
 *
 * ⚠ The "must differ from current" check is LAST, and that is deliberate: it is
 * the only one that compares against something the user has already typed
 * correctly, so reaching it means everything else was fine.
 */
export function validatePasswordChange(v: PasswordChangeInput): string | null {
  if (!v.current || !v.next || !v.confirm) return 'All fields are required.';
  if (v.next.length < MIN_PASSWORD_LENGTH) {
    return `New password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
  }
  if (v.next !== v.confirm) return 'New passwords do not match.';
  if (v.next === v.current) return 'New password must differ from current.';
  return null;
}

/**
 * ⚠ DELIBERATELY LOOSE. The old client's own pattern: something, an `@`,
 * something, a dot, something. It is not RFC-correct and must not become so —
 * a stricter pattern rejects addresses that genuinely work, and the real
 * verification is the confirmation link, which nobody can fake past.
 */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Normalise an address the way the change flow stores it.
 *
 * ⚠ TRIMMED AND LOWERCASED, because the comparison below has to be
 * case-insensitive: `Josh@…` and `josh@…` are the same mailbox everywhere that
 * matters, and treating them as different would send a "confirm your new
 * address" email to the address the user already has.
 */
export const normaliseEmail = (raw: string): string => String(raw ?? '').trim().toLowerCase();

export function validateEmailChange(
  nextRaw: string, password: string, currentEmail: string,
): string | null {
  const next = normaliseEmail(nextRaw);
  if (!next || !password) return 'Both fields required.';
  if (!EMAIL_SHAPE.test(next)) return 'That does not look like a valid email.';
  if (next === normaliseEmail(currentEmail)) return 'That is already your email.';
  return null;
}

// ---------------------------------------------------------------------------
// Profile display
// ---------------------------------------------------------------------------

/**
 * The "Account created" line.
 *
 * ⚠ Takes the raw timestamp and returns an EM DASH for anything unreadable,
 * rather than "Invalid Date". A profile card is the screen a user reads when
 * something has already gone wrong and they are about to contact support;
 * printing a JavaScript error string there reads as a broken account.
 */
export function formatAccountCreated(
  raw: string | null | undefined,
  locale?: string,
): string {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(locale, { dateStyle: 'long', timeStyle: 'short' });
}
